import React, { useState } from "react";
import { Modal, Button, message, Typography } from "antd";
import {
  CopyOutlined,
  CheckCircleOutlined,
  MailOutlined,
  WarningOutlined,
} from "@ant-design/icons";

const { Text } = Typography;

// Admin-driven password reset — result modal.
// Copy flow: shows the one-time temp password with a copy button.
// Email flow: confirms the send (or warns if the email failed).
// The plaintext lives only in `result` (cleared by the parent on close).
export const ResetPasswordResultModal = ({ open, onClose, result }) => {
  const [copied, setCopied] = useState(false);
  const email = result?.email;
  const delivery = result?.delivery;
  const tempPassword = result?.temp_password;
  const emailSent = result?.email_sent;

  const copyToClipboard = () => {
    if (!tempPassword) return;
    navigator.clipboard.writeText(tempPassword).then(() => {
      setCopied(true);
      message.success("Temporary password copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleClose = () => {
    setCopied(false);
    onClose();
  };

  const emailFailed = delivery === "email" && emailSent === false;

  return (
    <Modal
      title={
        <div className="flex items-center gap-3">
          <CheckCircleOutlined className="text-green-500 text-2xl" />
          <div className="text-lg font-semibold">Password reset</div>
        </div>
      }
      open={open}
      onCancel={handleClose}
      width={520}
      centered
      footer={
        <div className="flex justify-end">
          <Button type="primary" onClick={handleClose} className="bg-blue-600">
            Done
          </Button>
        </div>
      }
    >
      <div className="py-4 flex flex-col gap-4">
        {delivery === "email" ? (
          emailFailed ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
              <WarningOutlined className="text-amber-600 text-xl mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-amber-900 mb-1">
                  Email failed to send
                </div>
                <div className="text-xs text-amber-700">
                  The password was reset, but the email to <strong>{email}</strong>{" "}
                  could not be sent. Re-run with &quot;Copy temp password&quot; to
                  retrieve a fresh one-time password.
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
              <MailOutlined className="text-blue-600 text-xl mt-0.5" />
              <div className="flex-1">
                <div className="text-sm font-semibold text-blue-900 mb-1">
                  Temporary password emailed
                </div>
                <div className="text-xs text-blue-700">
                  A temporary password was emailed to <strong>{email}</strong>.
                  They will be asked to set a new password on their next login.
                </div>
              </div>
            </div>
          )
        ) : (
          <>
            <Text className="text-sm text-gray-700">
              Share this one-time temporary password with <strong>{email}</strong>{" "}
              via a secure channel. They will be asked to set a new password on
              their next login.
            </Text>
            <div className="flex items-center justify-between gap-3 p-3 bg-[#F4F5F7] border border-[#DFE1E6] rounded-lg">
              <Text className="font-mono text-base font-semibold tracking-wide break-all">
                {tempPassword}
              </Text>
              <Button
                type="text"
                icon={
                  copied ? (
                    <CheckCircleOutlined className="text-green-500" />
                  ) : (
                    <CopyOutlined />
                  )
                }
                onClick={copyToClipboard}
              >
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};
